import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, Search, Shield, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const FundsNotShowingDerivationPaths = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Funds Not Showing? Derivation Paths Explained (Guide)</title>
        <meta name="description" content="Wallet shows zero balance? Learn derivation paths, accounts, and safe checks to locate funds. Understand when a deep scan helps. Non-custodial. Free assessment." />
        <meta name="keywords" content="wallet shows zero balance, funds not showing, derivation path, BIP44, wallet accounts, hidden funds, deep wallet scan" />
        <link rel="canonical" href="https://arcana-crypto.com/funds-not-showing-derivation-paths.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
              Funds Not Showing: Derivation Paths, Accounts, and What to Check
            </h1>
            <p className="section-intro" style={{fontSize: '18px', marginBottom: '40px'}}>
              A <strong>wallet showing "0"</strong> does not always mean funds are gone. Often, the wallet 
              is simply looking in the <strong>wrong place</strong> due to <strong>derivation path</strong> or 
              account mismatch.
            </p>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Often a <strong>derivation path/account</strong> issue, not lost funds.</li>
              <li>Wallet defaults vary (<strong>BIP44/49/84</strong>).</li>
              <li>Address hints/xpubs speed validation.</li>
              <li><strong>Deep scans</strong> help, but are not magic.</li>
              <li>Non-custodial analysis is possible.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Confirm network and wallet type.</li>
                <li>Check <strong>multiple accounts</strong> (Account 0, 1, 2, etc.).</li>
                <li>Verify <strong>address formats</strong> (Legacy, SegWit, Native SegWit).</li>
                <li>Search by known receiving address on blockchain explorer.</li>
                <li>Record wallet settings (path options, gap limits).</li>
              </ul>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not reset/reinstall blindly.</li>
                <li>Avoid importing seeds into random wallets.</li>
                <li>Do not assume scam immediately.</li>
                <li>Do not share seeds to get help.</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Search size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                Funds can appear "missing" if the wallet restores under a different <strong>derivation path/account index</strong>. 
                A <strong>deep scan</strong> checks relevant paths and indices to match derived addresses to on-chain history.
              </p>
              <p style={{marginTop: '16px'}}>
                For example, Bitcoin wallets can use <strong>BIP44</strong> (m/44'/0'/0'/0), <strong>BIP49</strong> (m/49'/0'/0'/0 for SegWit), 
                or <strong>BIP84</strong> (m/84'/0'/0'/0 for Native SegWit). If you created the wallet with one path but restore 
                with another, your balance shows zero even though funds exist on-chain.
              </p>
              <p style={{marginTop: '16px'}}>
                Our <strong>extensive derivation path analysis</strong> systematically checks standard and non-standard 
                paths across multiple account indices to locate your funds. This is done <strong>non-custodially</strong> - 
                we identify where funds are, you access them yourself.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <p className="legal-content" style={{marginBottom: '16px'}}>
                Non-custodial, offline work, encrypted comms, data minimisation and deletion aligned 
                with GDPR principles.
              </p>
              <ul className="column-list">
                <li>We identify derivation paths and addresses - you access funds</li>
                <li>No custody or control of client assets</li>
                <li>Offline processing with encrypted communications</li>
                <li>Results delivered securely with clear instructions</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="about-card">
              <p className="legal-content">
                <strong>More feasible</strong> with at least one known address/xpub and knowledge of the original 
                wallet software. Address hints dramatically reduce search space.
              </p>
              <p className="legal-content" style={{marginTop: '16px'}}>
                <strong>Harder</strong> with custom derivation paths, no address hints, and uncertainty about 
                original wallet configuration. Assessment determines search scope.
              </p>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Network (BTC/ETH/LTC/DOGE)</li>
                <li>Original wallet/app and current wallet/app</li>
                <li>Known addresses/xpubs/txids (critical for validation)</li>
                <li>Approximate dates and amounts</li>
                <li>Passphrase usage or multi-account setup</li>
                <li>Any custom derivation path knowledge</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="Can funds be hidden without being stolen?"
                answer="Yes. Funds existing on a different derivation path appear 'hidden' to wallets scanning the wrong path. They're still on-chain and recoverable."
              />
              <FAQItem 
                question="Is a deep scan safe?"
                answer="Yes when done non-custodially and offline. We derive addresses and check balances - we never access private keys or move funds."
              />
              <FAQItem 
                question="Do you need my seed?"
                answer="Not via form/chat. For derivation path analysis, seed handling is discussed securely if required."
              />
              <FAQItem 
                question="How long does a scan take?"
                answer="Standard scans: 5-15 minutes. Deep scans checking extensive paths: 2-6 hours depending on scope."
              />
              <FAQItem 
                question="What if it's a custom path?"
                answer="Feasibility depends on hints. Completely custom paths with no clues are extremely difficult. Assessment determines scope."
              />
              <FAQItem 
                question="Can you do this for hardware wallets?"
                answer="Often yes. Hardware wallets use standard derivation paths. If you have the seed, path analysis can locate funds."
              />
              <FAQItem 
                question="Do you guarantee you will find funds?"
                answer="No. We scan probable paths based on wallet standards. Custom or unusual configurations may not be found."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Wallet Shows Zero Balance?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Request a free, confidential assessment
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              FREE DERIVATION PATH ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Non-custodial • Confidential • Professional
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/services" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                All Recovery Services →
              </a>
              <a href="/crypto-recovery-service-uk" className="card-link" style={{fontSize: '16px'}}>
                UK Services →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FundsNotShowingDerivationPaths;
